/**
 * The phone Quick Bytes header - everything above the grid.
 *
 *   Quick Bytes                         [Filters 2]
 *   12 CONCEPTS
 *   [  All Bytes  |  🔖 Bookmarks  ]
 *   (due today ×) (algebra ×) →
 *
 * The same shape as the Knowledge Archive's phone header, so the two
 * Mindspace pages with filters read alike: a labelled Filters button that
 * counts what is on (it was an unlabelled icon up by Back), a mono count, and
 * the active filters as a chip row. The All | Bookmarks switch is the app's
 * segmented control: the old tab strip drew its active tab as a grey fill on
 * a lavender page, and nothing said which tab you were on.
 *
 * Bookmarks is which collection, not a filter, so it stays on the page
 * rather than in the sheet, and the sheet's "Clear all" leaves it alone.
 */

import { useState } from 'react';
import { Bookmark, Layers } from 'lucide-react';
import { SegmentedControl } from '@ai-tutor/ui';
import { ActiveFilterChips } from '@/components/features/mindspace/filters/ActiveFilterChips';
import {
  FiltersButton,
  PhoneFilterSheet,
} from '@/components/features/mindspace/filters/PhoneFilters';
import { QuickBytesFilterPanel } from '@/components/features/mindspace/quick-bytes/QuickBytesFilterPanel';
import { useQuickBytesHighlights } from '@/hooks/mindspace/useRevision';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';

const VIEWS = [
  { value: 'all', label: 'All Bytes', icon: Layers },
  { value: 'bookmarks', label: 'Bookmarks', icon: Bookmark },
] as const;

export function QuickBytesPhoneHeader() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { bookmarked, setFilters, asHighlightsFilters } = useTagFilters();
  // The grid's own query - same key, so this reads its cache, no request.
  const { data } = useQuickBytesHighlights(asHighlightsFilters());
  const total = data?.pages[0]?.total ?? null;

  return (
    <header className='flex-shrink-0 border-b border-border px-4 pb-2 pt-2'>
      <div className='flex items-center justify-between gap-3'>
        <h2 className='min-w-0 text-xl font-semibold leading-tight text-foreground'>
          Quick Bytes
        </h2>
        <FiltersButton
          open={filtersOpen}
          onClick={() => setFiltersOpen(true)}
        />
      </div>
      <p
        data-testid='quick-bytes-count'
        className='mt-0.5 flex h-5 items-center font-mono text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground'
      >
        {countText(total, bookmarked)}
      </p>

      <SegmentedControl
        label='Highlights view'
        options={VIEWS}
        value={bookmarked ? 'bookmarks' : 'all'}
        onChange={v => setFilters({ bookmarked: v === 'bookmarks' })}
        size='sm'
        className='mt-2 flex w-full'
        segmentClassName='tap-target flex-1 py-1.5 text-sm'
      />

      {/* `mt-1`: the segments' 44px tap areas reach past the control's
          edge, and the chips' reach up to the row's - flush, the chips won
          the overlap. */}
      <div className='mt-1'>
        <ActiveFilterChips variant='row' />
      </div>

      <PhoneFilterSheet
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        doneLabel={showLabel(total)}
      >
        <QuickBytesFilterPanel showViewTabs={false} layout='sheet' />
      </PhoneFilterSheet>
    </header>
  );
}

/** "12 concepts" / "3 bookmarked"; a held blank before the count lands. */
function countText(total: number | null, bookmarked: boolean) {
  if (total == null) return ' ';
  if (bookmarked) return `${total.toLocaleString()} bookmarked`;
  return `${total.toLocaleString()} concept${total === 1 ? '' : 's'}`;
}

function showLabel(total: number | null) {
  if (total == null) return 'Show bytes';
  return `Show ${total.toLocaleString()} concept${total === 1 ? '' : 's'}`;
}
